import axios from 'axios';

import { BACKEND_URL } from "./backendUrl";
const API = `${BACKEND_URL}/api`;

// ── eSUN equivalents for generic SICNOVA/BambuLab materials ────────────
// Maps a material id (or material name keyword) to the eSUN equivalent.
const ESUN_EQUIVALENTS = {
  // PLA family
  "pla-basic":  { url: "https://www.esun3d.com/pla-pro-product/",  label: "COMPRAR PLA+ EN ESUN" },
  "pla-tough":  { url: "https://www.esun3d.com/pla-pro-product/",  label: "COMPRAR PLA+ EN ESUN" },
  // PETG
  "petg":       { url: "https://www.esun3d.com/petg-product/",     label: "COMPRAR PETG EN ESUN" },
  // ABS
  "abs":        { url: "https://www.esun3d.com/abs-pro-product/",  label: "COMPRAR ABS+ EN ESUN" },
  // TPU
  "tpu":        { url: "https://www.esun3d.com/etpu-95a-product/", label: "COMPRAR TPU 95A EN ESUN" },
  // Nylon / PA
  "nylon-pa":   { url: "https://www.esun3d.com/epa-product/",      label: "COMPRAR PA EN ESUN" },
};

// BambuLab via Sicnova - prioritized URLs by material type
const BAMBULAB_URLS = {
  "pla":  "https://tienda.sicnova3d.com/bambulab-filamentos/17296-6202-pla-basic-de-bambu-lab-filamento-para-impresion-3d",
  "abs":  "https://tienda.sicnova3d.com/bambulab-filamentos/17292-abs-bambu-lab-filamento-de-impresion-3d",
  "petg": "https://tienda.sicnova3d.com/bambulab-filamentos/17563-petg-hf-de-bambu-lab-filamento-para-impresion-3d",
  "tpu":  "https://tienda.sicnova3d.com/bambulab-filamentos/17556-tpu-95a-de-bambu-lab-filamento-para-impresion-3d",
  "asa":  "https://tienda.sicnova3d.com/bambulab-filamentos/17552-asa-de-bambu-lab-filamento-para-impresion-3d",
};

const findBambuLabUrl = (materialName) => {
  const name = (materialName || "").toLowerCase();
  for (const [key, url] of Object.entries(BAMBULAB_URLS)) {
    if (name.includes(key) && !name.includes("formlabs") && !name.includes("markforged")) {
      return { key, url };
    }
  }
  return null;
};

const findEsunEquivalent = (material) => {
  const id = (material.id || "").toLowerCase();
  if (ESUN_EQUIVALENTS[id]) return ESUN_EQUIVALENTS[id];
  // Match by name keyword (avoid double-matching specialty materials)
  const name = (material.name || "").toLowerCase();
  if (name === "abs" || name.startsWith("abs ")) return ESUN_EQUIVALENTS["abs"];
  if (name === "petg" || name.startsWith("petg ")) return ESUN_EQUIVALENTS["petg"];
  if (name.startsWith("tpu") && name.includes("flex")) return ESUN_EQUIVALENTS["tpu"];
  if (name.includes("nylon") || name === "pa" || name.startsWith("pa ")) return ESUN_EQUIVALENTS["nylon-pa"];
  return null;
};

/**
 * Returns an array of buy options for a material.
 * Each option: { url, label, destination, primary }
 * The first option (primary=true) is the recommended one (rendered prominently).
 * Subsequent options are shown as secondary buttons.
 */
export const getBuyUrls = (material) => {
  if (!material) return [];

  const brand = (material.brand || "").toLowerCase();
  const name = (material.name || "").toLowerCase();
  const options = [];

  // ── eSUN-branded material → only its own direct URL ────────────────
  if (brand.includes("esun")) {
    if (material.esun_url) {
      options.push({
        url: material.esun_url,
        label: "COMPRAR EN ESUN",
        destination: "esun",
        primary: true,
      });
    } else {
      options.push({
        url: "https://www.esun3d.com/filaments/",
        label: "COMPRAR EN ESUN",
        destination: "esun",
        primary: true,
      });
    }
    return options;
  }

  // ── BambuLab-branded material → BambuLab URL + eSUN equivalent ────
  if (brand.includes("bambu")) {
    const bambu = findBambuLabUrl(name);
    if (bambu) {
      options.push({
        url: bambu.url,
        label: "COMPRAR EN BAMBU LAB (SICNOVA)",
        destination: "bambulab",
        primary: true,
      });
    }
    const esun = findEsunEquivalent(material);
    if (esun) {
      options.push({
        url: esun.url,
        label: esun.label,
        destination: "esun",
        primary: false,
      });
    }
    if (options.length === 0 && material.sicnova_url) {
      options.push({
        url: material.sicnova_url,
        label: "COMPRAR EN SICNOVA",
        destination: "sicnova",
        primary: true,
      });
    }
    return options;
  }

  // ── Generic / SICNOVA materials with possible BambuLab + eSUN match ──
  const bambu = findBambuLabUrl(name);
  if (bambu) {
    options.push({
      url: bambu.url,
      label: "COMPRAR BAMBU LAB (RECOMENDADO)",
      destination: "bambulab",
      primary: true,
    });
  }
  const esun = findEsunEquivalent(material);
  if (esun) {
    options.push({
      url: esun.url,
      label: esun.label,
      destination: "esun",
      primary: options.length === 0,
    });
  }
  if (material.sicnova_url) {
    options.push({
      url: material.sicnova_url,
      label: options.length === 0 ? "COMPRAR EN SICNOVA" : "VER OTRAS OPCIONES",
      destination: "sicnova",
      primary: options.length === 0,
    });
  }

  return options;
};

// Track click and send to GTM
export const trackBuyClick = async (material, destination, url, userId = null) => {
  try {
    await axios.post(`${API}/track/click`, {
      material_id: material.id,
      material_name: material.name,
      destination: destination,
      url: url,
      user_id: userId,
    });
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.debug('Error tracking click:', error.message);
  }

  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'buy_button_click',
      material_id: material.id,
      material_name: material.name,
      material_brand: material.brand,
      material_category: material.category,
      destination: destination,
      destination_url: url,
      price_per_kg: material.price_per_kg,
    });
  }
};

// Handle buy button click with tracking
export const trackAndNavigate = (e, material, buyOption, user) => {
  trackBuyClick(material, buyOption.destination, buyOption.url, user?.id);
  // Let the default <a> behavior handle navigation (avoids popup blockers)
};

// Backward-compatible single-result helper (returns first option in old shape)
export const getBuyUrl = (material) => {
  const opts = getBuyUrls(material);
  if (opts.length === 0) return null;
  return {
    url: opts[0].url,
    label: opts[0].label,
    priority: opts[0].primary,
    destination: opts[0].destination,
  };
};
